



























































import { Vue, Component, Ref, Watch } from "vue-property-decorator";
import { loadClient } from "@/data/repository/ClientRepository";
import router from "@/router/router";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ProjectEditingSettings, ProjectManagementSettings } from "@/data/models/Settings";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { defineOwnProjectManagementSettingsClientAccess } from "@/permissions/ClientPermissions";
import { createDefaultProjectEditingSettings } from "@/data/models/Client";
import { updateProjectManagementSettings } from "@/data/repository/SettingsRepository";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_BUTTON_SETTINGS_SAVE, COLOR_BUTTON_SETTINGS_EDIT } from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import { toNumber } from "lodash";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ProjectEditingSettingsComponent extends Vue {
  isLoading = false;
  isInitializing = true;
  isEditMode = false;

  defaultDryingDurationInDays = 14;
  defaultFollowUpInspectionIntervalInDays = 0;

  showingSuccessSnackbar = false;
  successSnackbarText = "";

  initialProjectEditingSettings: ProjectEditingSettings = createDefaultProjectEditingSettings();

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  @Watch("projectManagementSettings")
  onProjectManagementSettingsChanged() {
    const projectEditingSettings = this.projectManagementSettings.projectEditingSettings;

    if (
      projectEditingSettings && 
      !projectEditingSettings.areDefaultProjectEditingSettings
    ) {
      this.initializeParameters(projectEditingSettings);  
    }
  }

  get buttonColor() {
    return themedFill(this.isEditMode
      ? COLOR_BUTTON_SETTINGS_SAVE
      : COLOR_BUTTON_SETTINGS_EDIT);
  }

  get buttonTextClass() {
    return themedFillTextClass(this.isEditMode
      ? COLOR_BUTTON_SETTINGS_SAVE
      : COLOR_BUTTON_SETTINGS_EDIT, "white--text");
  }

  get hasChanged() {
    return (
      this.defaultDryingDurationInDays !== this.initialProjectEditingSettings.defaultDryingDurationInDays ||
      this.defaultFollowUpInspectionIntervalInDays !== this.initialProjectEditingSettings.defaultFollowUpInspectionIntervalInDays
    );
  }

  get projectManagementSettings() {
    return ProjectManagementSettingsModule.projectManagementSettings;
  }

  get projectEditingSettings() {
    return this.projectManagementSettings.projectEditingSettings;
  }

  get isAllowedToDefineOwnProjectManagementSettings() {
    return defineOwnProjectManagementSettingsClientAccess();
  }

  get readonly() {
    return !this.isEditMode || !this.isAllowedToDefineOwnProjectManagementSettings;
  }

  initializeParameters(
    projectEditingSettings: ProjectEditingSettings,
  ) {
    this.defaultDryingDurationInDays = projectEditingSettings.defaultDryingDurationInDays;
    this.defaultFollowUpInspectionIntervalInDays = projectEditingSettings.defaultFollowUpInspectionIntervalInDays;

    this.initialProjectEditingSettings = projectEditingSettings;

    this.isEditMode = false;
  }

  async created() {
    this.isInitializing = true;
    const client = await loadClient();
    const dryingParameters = client?.dryingParameters;
    const projectEditingSettings = this.projectManagementSettings.projectEditingSettings;

    if (projectEditingSettings && !projectEditingSettings.areDefaultProjectEditingSettings) {
      this.initializeParameters(projectEditingSettings);
    }
    else {
      this.initializeParameters({
        defaultDryingDurationInDays: dryingParameters?.defaultDryingDurationInDays ?? 14,
        defaultFollowUpInspectionIntervalInDays: dryingParameters?.defaultFollowUpInspectionIntervalInDays ?? 7,
      });
    }
    this.isInitializing = false;
  }

  async onSave() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      return;
    }

    if (!this.hasChanged) {
      return;
    }

    this.isLoading = true;

    const projectEditingSettingsUpdate: ProjectEditingSettings = {
      defaultDryingDurationInDays: toNumber(this.defaultDryingDurationInDays),
      defaultFollowUpInspectionIntervalInDays: toNumber(this.defaultFollowUpInspectionIntervalInDays),

      areDefaultProjectEditingSettings: false,
    }

    const update: ProjectManagementSettings = {
      ...this.projectManagementSettings,
      projectEditingSettings: projectEditingSettingsUpdate,
    }

    await updateProjectManagementSettings(update);

    this.showingSuccessSnackbar = true;
    this.successSnackbarText = this.$t("common.changesSaved").toString();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.initialProjectEditingSettings = update.projectEditingSettings!!;
    this.isEditMode = false;
    this.isLoading = false;
  }

  async onBack() {
    if (this.isEditMode) {
      if (this.hasChanged) {
        const result = await this.confirmationDialogComponent.show({
          title: this.$t("common.confirmationDialog.cancelTitle").toString(),
          message: this.$t("common.confirmationDialog.cancelMessage").toString(),
        });

        if (result) {
          this.initializeParameters(this.initialProjectEditingSettings);
          return;
        }

        return;
      }

      this.isEditMode = false;
      return;
    }

    router.back();
  }

  mounted() {
    this.setToolbarTitle();
  }

  setToolbarTitle() {
    const baseTitle = this.$t("pages.settings").toString();
    const currentPageTitle = this.$t("settings.projectManagement.projectEditingSettings.title").toString();

    const toolbarTitle = baseTitle + " > " + currentPageTitle;

    ToolbarContentModule.setToolbarTitle(toolbarTitle);
  }
}
